import {
  ViveApiBaseService,
  ViveApiRequestOptions
} from './vive-api-base.service';
import {
  ESignProviderDtoNameEnum,
  ESignTemplateDto,
  ESignTemplateDtoTemplateTypeEnum,
  ESignTemplateVendorFieldDto,
  EsignTemplatesAcroFormFieldResponseDto,
  EsignTemplatesAcroFormRequestDto,
  EsignTemplatesFileVersionResponseDto,
  EsignTemplatesGetFilePresignedUrlResponseDto,
  EsignTemplatesResignImpactResponseDto,
  EsignTemplatesUnmappedFieldReportResponseDto,
  EsignTemplatesUpsertRequestDto
} from './vive-api-models';

class EsignTemplatesService extends ViveApiBaseService {
  public async findOneByCompanyIdAndTemplateTypeAndProviderName(params: {
    options?: ViveApiRequestOptions;
    pathParams: {
      templateType: ESignTemplateDtoTemplateTypeEnum;
      companyId: number;
      esignProviderName: ESignProviderDtoNameEnum;
    };
  }): Promise<ESignTemplateDto> {
    const { options, pathParams } = params;
    return await this.sendGet<ESignTemplateDto>({
      apiEndpoint:
        'esign-templates/:templateType/company/:companyId/esign-provider/:esignProviderName',
      options,
      pathParams
    });
  }

  /**
   * The client-defined fields a vendor must answer before this template can be
   * stamped. Empty for every standard template.
   */
  public async findVendorInputFields(params: {
    options?: ViveApiRequestOptions;
    pathParams: {
      esignTemplateId: number;
    };
  }): Promise<ESignTemplateVendorFieldDto[]> {
    const { options, pathParams } = params;
    return await this.sendGet<ESignTemplateVendorFieldDto[]>({
      apiEndpoint: 'esign-templates/:esignTemplateId/vendor-fields',
      options,
      pathParams
    });
  }

  /**
   * Every AcroForm field in the stored template file beside the mapping that
   * fills it, plus any mapping naming a field the file no longer has.
   */
  public async findAcroFormFields(params: {
    options?: ViveApiRequestOptions;
    pathParams: {
      esignTemplateId: number;
    };
  }): Promise<EsignTemplatesAcroFormFieldResponseDto[]> {
    const { options, pathParams } = params;
    return await this.sendGet<EsignTemplatesAcroFormFieldResponseDto[]>({
      apiEndpoint: 'esign-templates/:esignTemplateId/acro-form-fields',
      options,
      pathParams
    });
  }

  /**
   * The same comparison against a file that has not been committed yet, so a
   * renamed field is found before the swap rather than after it. Persists
   * nothing.
   */
  public async findAcroFormFieldsForCandidateFile(params: {
    options?: ViveApiRequestOptions;
    pathParams: {
      esignTemplateId: number;
    };
    data: EsignTemplatesAcroFormRequestDto;
  }): Promise<EsignTemplatesAcroFormFieldResponseDto[]> {
    const { options, pathParams, data } = params;
    return await this.sendPost<EsignTemplatesAcroFormFieldResponseDto[]>({
      apiEndpoint: 'esign-templates/:esignTemplateId/acro-form-fields',
      options,
      pathParams,
      data
    });
  }

  /**
   * Every live VIVE template holding a field that neither VIVE's standard
   * handling nor the template's own mapping fills. Each one prints blank on
   * agreements that have already been signed. Writes nothing, and returns the
   * same answer however often it is run.
   */
  public async reportUnmappedTemplateFields(params: {
    options?: ViveApiRequestOptions;
  }): Promise<EsignTemplatesUnmappedFieldReportResponseDto[]> {
    const { options } = params;
    return await this.sendGet<EsignTemplatesUnmappedFieldReportResponseDto[]>({
      apiEndpoint: 'esign-templates/unmapped-field-report',
      options
    });
  }

  /**
   * How many signatures a mapping change would ask to be taken again. Show this
   * before saving a mapping: the change moves the template checksum, and there
   * is no undo.
   */
  public async countSignaturesRequiringResign(params: {
    options?: ViveApiRequestOptions;
    pathParams: {
      esignTemplateId: number;
    };
  }): Promise<EsignTemplatesResignImpactResponseDto> {
    const { options, pathParams } = params;
    return await this.sendGet<EsignTemplatesResignImpactResponseDto>({
      apiEndpoint: 'esign-templates/:esignTemplateId/resign-impact',
      options,
      pathParams
    });
  }

  public async findTemplateFileVersions(params: {
    options?: ViveApiRequestOptions;
    pathParams: {
      esignTemplateId: number;
    };
  }): Promise<EsignTemplatesFileVersionResponseDto[]> {
    const { options, pathParams } = params;
    return await this.sendGet<EsignTemplatesFileVersionResponseDto[]>({
      apiEndpoint: 'esign-templates/:esignTemplateId/file-versions',
      options,
      pathParams
    });
  }

  /** Audited as a download on every call. */
  public async getTemplateFileVersionPresignedUrl(params: {
    options?: ViveApiRequestOptions;
    pathParams: {
      esignTemplateId: number;
      templateFileId: number;
    };
  }): Promise<EsignTemplatesGetFilePresignedUrlResponseDto> {
    const { options, pathParams } = params;
    return await this.sendGet<EsignTemplatesGetFilePresignedUrlResponseDto>({
      apiEndpoint:
        'esign-templates/:esignTemplateId/file-versions/:templateFileId/presigned-url',
      options,
      pathParams
    });
  }

  public async upsertESignTemplate(params: {
    options?: ViveApiRequestOptions;
    data: EsignTemplatesUpsertRequestDto;
  }): Promise<ESignTemplateDto> {
    const { options, data } = params;
    return await this.sendPut<ESignTemplateDto>({
      apiEndpoint: 'esign-templates',
      options,
      data
    });
  }

  public async archiveESignTemplate(params: {
    options?: ViveApiRequestOptions;
    pathParams: {
      esignTemplateId: number;
    };
  }): Promise<void> {
    const { options, pathParams } = params;
    return await this.sendDelete<void>({
      apiEndpoint: 'esign-templates/:esignTemplateId',
      options,
      pathParams
    });
  }
}

export const esignTemplatesService = new EsignTemplatesService();
